/**
 * NatureLayer — the orchestrator for the Tree & Nature system. Mounts/unmounts
 * biome-tinted vegetation (trees/bushes/grass) on a level's standable
 * platforms, behind the ISprite seam (lift-ready checklist §1: logic never
 * touches Pixi directly — it talks to `createSprite` + `PixiOverlayHost`'s
 * `WorldLayerHandle`, both substrate-agnostic interfaces).
 *
 * Lifecycle: `install()` wires LEVEL_LOADED → resolve the biome (same
 * `resolveScene` precedence the atmosphere layer uses, so vegetation can never
 * desync from the mounted preset) → `placeVegetation` (pure, seeded) → mint one
 * ISprite per plant into a per-layer container → `unmount()` destroys both.
 * Mirrors AtmosphereLayer's install/mount/unmount shape so the two compose.
 *
 * Best-effort + budget-aware (BUILD_STANDARDS §3): a not-ready renderer or a
 * failed texture load yields a quieter scene (the sprite stays at alpha 0) —
 * never a throw. Authored landmarks do not require a procedural foliage profile;
 * an empty combined placement creates no container.
 */
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { gameLogger } from '../../../utils/GameLogger';
import { PixiOverlayHost } from '../../rendering/PixiOverlayHost';
import { createSprite } from '../../rendering/utils/Sprite';
import { Z_LAYERS } from '../../rendering/Z_LAYERS';
import type { ISprite, WorldLayerHandle } from '../../rendering/IRenderer';
import { resolveScene } from '../atmosphere/scene/resolveScene';
import { hashSeed } from '../atmosphere/atmospherePlacement';
import { resolveMapVegetation, biomeHasVegetation } from './natureConfig';
import { placeAuthoredNature, placeVegetation, ARCHITECTURE_FIXTURE_DEPTH } from './naturePlacement';
import { generateVegetationTexture } from './natureTextures';
import { natureLiveConfig } from './natureLiveConfig';
import type { PlatformerLevel } from '../../../shared/CharacterTypes';
import type { TownPropRenderBand } from './props/townPropTemplates';

interface MountedLayer {
  containers: Map<TownPropRenderBand, WorldLayerHandle>;
  sprites: ISprite[];
}

class NatureLayerImpl {
  private installed = false;
  private current: MountedLayer | null = null;
  private pendingLevelId: string | null = null;
  private readinessGeneration = 0;

  /** Debug surface for VQA probes — live plant count + biome. */
  private _biome: string | null = null;

  install(): void {
    if (this.installed) return;
    this.installed = true;
    try { (globalThis as { __nature?: unknown }).__nature = this; } catch { /* no global */ }
    const bus = EventBus.getInstance();
    bus.on(EVENTS.LEVEL_LOADED, (data: { id?: string }) => {
      const levelId = data?.id;
      if (!levelId) { this.unmount(); return; }
      this.unmount();
      this.pendingLevelId = levelId;
      const generation = this.readinessGeneration;
      // Wait for the overlay's worldContainer (same gate as AtmosphereLayer —
      // createWorldLayerContainer throws before ensureReady resolves).
      void PixiOverlayHost.ensureReady()
        .then(() => {
          if (this.readinessGeneration === generation && this.pendingLevelId === levelId) this.mount(levelId);
        })
        .catch((e) => gameLogger.warn('vfx', `[Nature] deferred mount skipped: ${(e as Error)?.message ?? e}`));
    });
    bus.on(EVENTS.LEVEL_UNLOADED, () => this.unmount());
  }

  /** Build vegetation for a level. Replaces any current layer. */
  mount(levelId: string): void {
    this.unmount();
    try {
      const mapSystem = ServiceRegistry.getInstance().get('MapSystem', { optional: true });
      const level = mapSystem?.getCurrentLevel?.() as PlatformerLevel | undefined;
      if (level?.id !== levelId || !level.platforms?.length) return;

      // Resolve the biome through the same precedence as the atmosphere layer
      // (level.scene.biome → presetForLevel → null) so vegetation tracks the
      // mounted preset — never desyncs.
      const scene = resolveScene(level);
      const biome = scene.biome || levelId;
      this._biome = biome;
      const seed = hashSeed(levelId);
      // Explicit props are reusable even in a new biome with no random foliage profile.
      const seededPlants = biomeHasVegetation(biome)
        ? placeVegetation(resolveMapVegetation(levelId, biome), level.platforms, seed, biome, natureLiveConfig) : [];
      const authoredPlants = placeAuthoredNature(level.scene?.natureProps ?? [], biome, level);
      const ordered = [...seededPlants, ...authoredPlants];
      // Layer children use insertion order. Preserve old ordering, then adopt fixtures above their hosts.
      const plants = [...ordered.filter(p => p.depth !== ARCHITECTURE_FIXTURE_DEPTH),
        ...ordered.filter(p => p.depth === ARCHITECTURE_FIXTURE_DEPTH)];
      if (plants.length === 0) return;

      const sprites: ISprite[] = [];
      const mounted: MountedLayer = { containers: new Map(), sprites };
      this.current = mounted;
      for (const plant of plants) {
        const band = plant.renderBand ?? 'vegetation';
        let container = mounted.containers.get(band);
        if (!container) {
          container = PixiOverlayHost.createWorldLayerContainer(
            band === 'architecture' ? Z_LAYERS.ARCHITECTURE : Z_LAYERS.VEGETATION);
          // Own each nonempty band's container before its first sprite can fail.
          mounted.containers.set(band, container);
        }
        // Generate (or fetch cached) the biome-tinted SVG dataUrl for this
        // plant's kind + canopy color, then mint the sprite. The sprite starts
        // at alpha 0 (hidden) until the texture loads, then reveals.
        const dataUrl = generateVegetationTexture(plant.textureKey, plant.kind, plant.tint, plant.template, plant.palette);
        const sprite = createSprite(null, {
          x: plant.x,
          y: plant.y,
          initialTextureKey: plant.textureKey,
          width: plant.width,
          height: plant.height,
          depth: plant.depth,
          alpha: 0, // hidden until texture loads
          parent: container,
        });
        // Own each partial result before any setup/load can fail synchronously.
        sprites.push(sprite);
        // Bottom-anchor so the plant's base sits on the platform top edge.
        sprite.setOrigin(0.5, 1);
        if (plant.flipX) sprite.setFlipX(true);

        if (sprite.hasTexture(plant.textureKey)) {
          sprite.setTexture(plant.textureKey);
          sprite.setAlpha(plant.alpha);
        } else {
          sprite.loadTexture(plant.textureKey, dataUrl)
            .then(() => {
              if (this.current !== mounted || !sprite.isActive()) return;
              sprite.setTexture(plant.textureKey);
              sprite.setAlpha(plant.alpha);
            })
            .catch((err) => {
              gameLogger.warn('vfx', `[Nature] texture load failed for ${plant.textureKey}: ${err}`);
              // Reveal anyway (EMPTY texture) so the slot isn't invisible forever.
              if (this.current === mounted && sprite.isActive()) sprite.setAlpha(plant.alpha);
            });
        }
      }

      gameLogger.info('vfx', `[Nature] mounted ${sprites.length} plants (biome="${biome}", level=${levelId})`);
    } catch (e) {
      this.unmount();
      gameLogger.warn('vfx', `[Nature] mount failed: ${(e as Error)?.message ?? e}`);
    }
  }

  /** Destroy the current vegetation layer. */
  unmount(): void {
    this.readinessGeneration++;
    this.pendingLevelId = null;
    this._biome = null;
    const current = this.current;
    this.current = null;
    if (!current) return;
    for (const s of current.sprites) { try { s.destroy(); } catch { /* gone */ } }
    for (const container of current.containers.values()) { try { container.destroy(); } catch { /* gone */ } }
  }

  /** Live plant count (VQA probe surface — mirrors __atmosphere.spriteCount). */
  spriteCount(): number {
    return this.current?.sprites.length ?? 0;
  }

  /** The currently-mounted biome id (VQA probe surface). */
  currentBiome(): string | null {
    return this._biome;
  }
}

export const NatureLayer = new NatureLayerImpl();
